import React from "react"
import PropTypes from "prop-types"
import cx from "classnames"
import dismissIcon from "../../../assets/images/notifications/notif-dismiss.svg"
import downloadedIcon from "../../../assets/images/notifications/notif-downloaded.svg"
import downloadingIcon from "../../../assets/images/notifications/notif-downloading.svg"
import errorIcon from "../../../assets/images/notifications/notif-error.svg"
import failedIcon from "../../../assets/images/notifications/notif-failed.svg"
import openIcon from "../../../assets/images/notifications/notif-open.svg"
import pdfIcon from "../../../assets/images/notifications/notif-file-pdf.svg"
import queuedIcon from "../../../assets/images/notifications/notif-queued.svg"
import retryIcon from "../../../assets/images/notifications/notif-retry.svg"
import successIcon from "../../../assets/images/notifications/notif-success.svg"
import s from "./NotificationItem.module.scss"

/**
 * One entry in the notification list — Figma "Notification List".
 *
 * The same card in both places it appears: the bell's popover and the
 * Notifications page. They differ only in width, so this is one component
 * rather than two that have to be kept in step.
 *
 * A notification may carry a file, and what the attachment shows is driven by
 * that file's status. The design draws the strip in three skins, and each one
 * is a different thing to say about the same request:
 *
 *   queued      blue   the request is lodged, nothing to do but wait
 *   downloading blue   bytes are moving, so a bar and a way to cancel
 *   downloaded  plain  it is on disk, so a way to open it
 *   failed      red    it did not arrive, so a way to ask again
 */

const TONE_ICONS = { error: errorIcon, success: successIcon }

/**
 * Skin, mark and wording per status.
 *
 * `spin` is the mark that should look like it is moving — Figma draws a ring
 * with a gap, which reads as stalled unless it turns.
 */
const FILE_STATUS = {
  queued: { icon: queuedIcon, label: "In Queue", skin: s.fileBusy, tone: s.statusBusy, spin: true },
  downloading: {
    icon: downloadingIcon,
    label: "Downloading",
    skin: s.fileBusy,
    tone: s.statusBusy,
    spin: true
  },
  downloaded: { icon: downloadedIcon, label: "Downloaded", skin: s.fileDone, tone: s.statusDone },
  failed: { icon: failedIcon, label: "Failed", skin: s.fileFailed, tone: s.statusFailed }
}

const FileAttachment = ({ file, onOpen, onDismiss, onRetry }) => {
  const status = FILE_STATUS[file.status] ?? FILE_STATUS.downloaded
  const inFlight = file.status === "downloading"

  /**
   * One slot, and what sits in it follows from the status.
   *
   * A queued request has nothing to offer yet — no button at all, rather than
   * a disabled one, because there is no action being withheld.
   */
  const action = () => {
    if (inFlight) {
      return {
        label: `Cancel download of ${file.name}`,
        icon: dismissIcon,
        size: 24,
        onClick: () => onDismiss?.(file)
      }
    }
    if (file.status === "failed") {
      return {
        label: `Retry download of ${file.name}`,
        icon: retryIcon,
        size: 24,
        onClick: () => onRetry?.(file)
      }
    }
    if (file.status === "queued") return null
    return {
      label: `Open ${file.name}`,
      icon: openIcon,
      size: 16,
      onClick: () => onOpen?.(file)
    }
  }

  const control = action()

  return (
    <div className={cx(s.file, status.skin)}>
      <div className={s.fileRow}>
        {/* The design ships a file mark at 25x24 — not square, so it is sized
            explicitly rather than dropped into a square slot. */}
        <img className={s.fileIcon} src={pdfIcon} alt="" width={25} height={24} />

        <div className={s.fileText}>
          <p className={s.fileName}>{file.name}</p>
          <div className={s.fileMeta}>
            {file.size && (
              <span className={s.fileSize}>
                {inFlight && file.total ? `${file.size} / ${file.total}` : file.size}
              </span>
            )}
            <img
              className={cx(s.fileStatusIcon, { [s.spinning]: status.spin })}
              src={status.icon}
              alt=""
              width={16}
              height={16}
            />
            {/* The design shows the word, not the server's explanation — but
                losing the reason entirely makes a failure unfixable, so it
                rides along as the label's tooltip. */}
            <span className={cx(s.fileStatus, status.tone)} title={file.reason || undefined}>
              {status.label}
            </span>
          </div>
        </div>

        {control && (
          <button
            type="button"
            className={s.fileAction}
            aria-label={control.label}
            onClick={control.onClick}
          >
            <img src={control.icon} alt="" width={control.size} height={control.size} />
          </button>
        )}
      </div>

      {/* Only while bytes are moving. A bar frozen at 100% under a finished
          download says nothing the tick beside it doesn't already say, and a
          bar under a queued request would be advancing on nothing. */}
      {inFlight && (
        <div
          className={s.progress}
          role="progressbar"
          aria-valuenow={file.progress ?? 0}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`Downloading ${file.name}`}
        >
          <span className={s.progressFill} style={{ width: `${file.progress ?? 0}%` }} />
        </div>
      )}
    </div>
  )
}

FileAttachment.propTypes = {
  file: PropTypes.shape({
    name: PropTypes.string.isRequired,
    size: PropTypes.string,
    /** Shown as "size / total" only while downloading. */
    total: PropTypes.string,
    status: PropTypes.oneOf(["queued", "downloading", "downloaded", "failed"]),
    progress: PropTypes.number,
    /** Why it failed, when the server said. Shown as the status tooltip. */
    reason: PropTypes.string
  }).isRequired,
  onOpen: PropTypes.func,
  onDismiss: PropTypes.func,
  onRetry: PropTypes.func
}

/**
 * The design's sentence for a download request.
 *
 * The two injected values are set in the heavy weight, which is the whole
 * point of writing it out rather than showing a bare path: the reader is
 * looking for whose export this was and when they asked for it.
 */
const RequestLine = ({ request }) => (
  <p className={s.description}>
    Download request from <strong className={s.requestValue}>“{request.user}”</strong> on{" "}
    <strong className={s.requestValue}>“{request.label}”</strong>
  </p>
)

RequestLine.propTypes = {
  request: PropTypes.shape({
    user: PropTypes.string.isRequired,
    /** Already formatted for reading — this component never touches a clock. */
    label: PropTypes.string.isRequired
  }).isRequired
}

const NotificationItem = ({
  notification,
  timeLabel,
  requestLabel,
  onOpenFile,
  onDismissFile,
  onRetryFile,
  className
}) => {
  const {
    project,
    title,
    description,
    request,
    tone = "error",
    read = false,
    file
  } = notification

  return (
    <article className={cx(s.item, { [s.read]: read }, className)}>
      {/* The accent down the left edge is the read state at a glance: the
          brand blue for unread, grey once it has been seen. */}
      <span className={s.accent} aria-hidden="true" />

      <div className={s.body}>
        <img className={s.toneIcon} src={TONE_ICONS[tone] ?? errorIcon} alt="" width={16} height={16} />

        <div className={s.content}>
          <div className={s.topRow}>
            <span className={s.project}>{project}</span>
            <span className={s.status}>
              <span className={s.time}>{timeLabel}</span>
              {!read && <span className={s.unread} aria-label="Unread" role="img" />}
            </span>
          </div>

          <h3 className={s.title}>{title}</h3>

          {request ? (
            <RequestLine request={{ user: request.user, label: requestLabel ?? "" }} />
          ) : (
            description && <p className={s.description}>{description}</p>
          )}
        </div>
      </div>

      {file && (
        <FileAttachment
          file={file}
          onOpen={onOpenFile}
          onDismiss={onDismissFile}
          onRetry={onRetryFile}
        />
      )}
    </article>
  )
}

NotificationItem.propTypes = {
  notification: PropTypes.shape({
    project: PropTypes.string,
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    /** A download request — renders the design's sentence instead of `description`. */
    request: PropTypes.shape({ user: PropTypes.string }),
    /** Which mark sits beside the text — the design draws two. */
    tone: PropTypes.oneOf(["error", "success"]),
    read: PropTypes.bool,
    file: PropTypes.object
  }).isRequired,
  /** Already formatted — "now", "15m", "1 day ago". See `relativeTime`. */
  timeLabel: PropTypes.string,
  /** When the request was made, already formatted. Only used with `request`. */
  requestLabel: PropTypes.string,
  onOpenFile: PropTypes.func,
  onDismissFile: PropTypes.func,
  onRetryFile: PropTypes.func,
  className: PropTypes.string
}

export default NotificationItem
